import { ROUTES } from "@stores/AccountStore";

/**
 * Who may open which screen — one map for the desktop LeftBar, the
 * MobileTabBar and PrivateRoute. Each used to carry its own copy of these
 * rules, and only Fields had a route guard, so a team member whose role hides
 * machines could still open /devices by URL and get a screen of 403s.
 */
export type Capability = "fields" | "devices" | "agent";

/** Routes not listed here are open to every signed-in user. */
export const ROUTE_CAPABILITY: Partial<Record<ROUTES, Capability>> = {
  [ROUTES.FIELDS]: "fields",
  [ROUTES.REPORTS]: "fields",
  [ROUTES.REPORT_VIEW]: "fields",
  [ROUTES.PRESCRIPTION]: "fields",
  [ROUTES.DEVICES]: "devices",
  [ROUTES.DEVICE_TECHNICAL]: "devices",
  [ROUTES.DEVICES_CALIBRATION]: "devices",
  [ROUTES.TRACKS]: "devices",
  [ROUTES.ANALYTICS]: "devices",
  [ROUTES.AGENT]: "agent",
};

/** The AccountStore getters the rules read. */
export interface CapabilitySource {
  canViewFields: boolean;
  canAccessDevices: boolean;
  isMember: boolean;
  subscriptionPaid: boolean;
}

export function hasCapability(s: CapabilitySource, capability: Capability | undefined): boolean {
  switch (capability) {
    case undefined:
      return true;
    // A role can hide fields & yield maps entirely (backend 403 FIELDS_NOT_ALLOWED).
    case "fields":
      return s.canViewFields;
    // The devices / tracks / analytics area, per the group's canAccessDevices.
    case "devices":
      return s.canAccessDevices;
    // Owner-only in v1, and paid: every chat turn costs LLM tokens.
    case "agent":
      return !s.isMember && s.subscriptionPaid;
  }
}

export const canOpenRoute = (s: CapabilitySource, route: ROUTES): boolean =>
  hasCapability(s, ROUTE_CAPABILITY[route]);
